// Pinned (clicked) chart elements. A selection is a (series key, bucket
// index) pair; clicking an element toggles it, so several can be pinned at
// once and the detail panel lists them with their time ranges.
import type { TimeBucket } from './chartBuckets';

export interface ChartSelection {
  key: string;
  index: number;
}

export const sameSelection = (a: ChartSelection, b: ChartSelection) => a.key === b.key && a.index === b.index;

export const isSelected = (selection: readonly ChartSelection[], key: string, index: number) =>
  selection.some((s) => s.key === key && s.index === index);

// Click an unpinned element to add it, a pinned one to remove it.
export function toggleSelection(selection: readonly ChartSelection[], next: ChartSelection): ChartSelection[] {
  return selection.some((s) => sameSelection(s, next))
    ? selection.filter((s) => !sameSelection(s, next))
    : [...selection, next];
}

const DAY_MS = 24 * 60 * 60 * 1000;

const dateOpts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

const sameDay = (a: number, b: number) => new Date(a).toDateString() === new Date(b).toDateString();

// "Mon, Oct 20" for day-or-wider buckets; "Mon, Oct 20 14:00 - 15:00" for
// intra-day buckets; both ends dated when a range crosses midnight. `end` is
// exclusive (a bucket ends where the next begins), so a date-granular range
// shows its last day, not the day after.
export function formatTimeRange(start: number, end: number): string {
  const date = (t: number) => new Date(t).toLocaleDateString(undefined, dateOpts);
  const time = (t: number) => new Date(t).toLocaleTimeString(undefined, timeOpts);
  if (end - start >= DAY_MS) {
    const last = end - 1;
    return sameDay(start, last) ? date(start) : `${date(start)} - ${date(last)}`;
  }
  if (sameDay(start, end - 1)) return `${date(start)} ${time(start)} - ${time(end)}`;
  return `${date(start)} ${time(start)} - ${date(end)} ${time(end)}`;
}

export interface SelectedBucketGroup {
  index: number;
  bucket: TimeBucket;
  range: string;
  // Pinned series keys in this bucket (a single-series chart uses 'single').
  keys: string[];
}

export interface SelectionSummary {
  groups: SelectedBucketGroup[];
  // Earliest start to latest end across everything pinned; null when empty.
  overall: { start: number; end: number; range: string } | null;
}

// Chronological groups (one per pinned bucket) plus the overall span.
export function summarizeSelection(buckets: readonly TimeBucket[], selection: readonly ChartSelection[]): SelectionSummary {
  const byIndex = new Map<number, string[]>();
  for (const s of selection) {
    if (!buckets[s.index]) continue;
    byIndex.set(s.index, [...(byIndex.get(s.index) ?? []), s.key]);
  }
  const groups = [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, keys]) => ({ index, bucket: buckets[index], range: formatTimeRange(buckets[index].start, buckets[index].end), keys }));
  if (groups.length === 0) return { groups, overall: null };
  const start = Math.min(...groups.map((g) => g.bucket.start));
  const end = Math.max(...groups.map((g) => g.bucket.end));
  return { groups, overall: { start, end, range: formatTimeRange(start, end) } };
}
